import { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import SaveButton from '../components/psc/lib/SaveButton'
import WithdrawAllVersionsModal, { useWithdrawConfirm } from '../components/psc/lib/WithdrawAllVersionsModal'
import ErrorMessage from '../components/psc/lib/ErrorMessage'
import CancelButton from '../components/psc/lib/CancelButton'
import DeleteButton from '../components/psc/lib/DeleteButton'
import { PscPhrase } from '../components/psc/psc-phrase';
import SingleSelect from '../components/psc/lib/SingleSelect'
import FilePaths, { FileButtons } from '../components/psc/lib/FilePaths'
import TextArea from '../components/psc/lib/TextArea'
import Title from '../components/psc/lib/Title'
import Status from '../components/psc/lib/Status'
import PickDate from '../components/psc/lib/PickDate'
import Applicability from '../components/psc/lib/Applicability'
import CustomFields from '../components/psc/lib/CustomFields'
import HeaderStatusInfo from '../components/psc/lib/HeaderStatusInfo'
import ManualUpversion from '../components/psc/lib/ManualUpversion'
import RelatedItems from '../components/psc/lib/RelatedItems'
import Loading from '../components/psc/lib/Loading'
import NoAccess from '../components/psc/lib/NoAccess'
import DocumentVersions from '../components/psc/lib/DocumentVersions';

import useAuthentication from '../hooks/useAuthentication';
import useUserManagement from '../hooks/useUserManagement';
import useCustomerDocumentStatuses from '../hooks/useCustomerDocumentStatuses';
import useCadmObject from '../hooks/useCadmObject';
import useProjectContents from '../hooks/useProjectContents';
import useBrowserTabTitle from '../hooks/useBrowserTabTitle';

const CustomerDocument = () => {
    const pscState = useSelector(state => state.pscState.value)
    const project = pscState.project
    const params = useParams()
    const isNew = params.objID === 'new'
    const projectID = Number(params.projectID)
    const objID = Number(params.objID)

    const { setBrowserTabTitle } = useBrowserTabTitle()

    const { currentUser } = useAuthentication();

    const [error, setError] = useState(false)
    const [errorConflict, setErrorConflict] = useState(false)
    const [projectSystems, setProjectSystems] = useState([])
    const [hasLoaded, setHasLoaded] = useState(isNew ? true: false)
    const [hasAccess, setHasAccess] = useState(true)
    const [traceability, setTraceability] = useState(null);
    const [traceabilityLoading, setTraceabilityLoading] = useState(false);
    
    const {
        userHasExplicitProjectAccess,
        userHasProjectAccessLevel,
    } = useUserManagement()

    const {
        initCustomFields,
        referenceStructure,
        stdDocumentMap,
        getStdDocument,
        getUnarchivedStdDocuments,
    } = useCadmObject('customer_documents')

    const {
        customerDocumentStatuses,
    } = useCustomerDocumentStatuses()

    const {
        updateCustomerDocument,
        deleteCustomerDocument,
        forceReloadCustomerDocuments,
    } = useProjectContents()

    const [ id, setID ] = useState(null)
    const [ updatedAt, setUpdatedAt ] = useState(null)
    const [ stdDocumentID, setStdDocumentID ] = useState(null)
    const [ reference, setReference] = useState('')
    const [ referenceWithoutVersion, setReferenceWithoutVersion] = useState('')
    const [ version, setVersion] = useState('')
    const [ title, setTitle] = useState('')
    const [ createDate, setCreateDate] = useState(null)
    const [ organisationCode, setOrganisationCode] = useState('')
    const [ elementCode, setElementCode] = useState(project?.element_code || '')
    const [ receiveDate, setReceiveDate] = useState(null)
    const [ note, setNote] = useState('')
    const [ customerDocumentModels, setCustomerDocumentModels ] = useState([])
    const [ customerDocumentStatusID, setCustomerDocumentStatusID] = useState(1)
    const [ finalVersionFilePath, setFinalVersionFilePath] = useState('')
    const [ filePaths, setFilePaths] = useState([])
    const [ customFields, setCustomFields ] = useState([])
    
    // Read-only
    const [ customerDocumentStatusName, setCustomerDocumentStatusName ] = useState('')
    const [ originalCustomerDocumentStatusID, setOriginalCustomerDocumentStatusID] = useState(1)
    const [ originalCustomerDocumentStatusName, setOriginalCustomerDocumentStatusName ] = useState('')
    const [ isUsed, setIsUsed] = useState(false)
    const [ characteristics, setCharacteristics ] = useState({})
    const [ relatedItems, setRelatedItems ] = useState([])
    const [ previousVersionRelatedItems, setPreviousVersionRelatedItems ] = useState([])
    
    // Form controls
    const [ submitActive, setSubmitActive ] = useState(false)
    const [ submitSpinner, setSubmitSpinner ] = useState(false)
    const formRef = useRef(null)
    const saveButtonRef = useRef(null)

    const stdDocument = useMemo(() => {
        return getStdDocument(stdDocumentID)
    }, [stdDocumentMap, stdDocumentID]);

    useEffect(() => {
        const status = customerDocumentStatuses.find((element) => element.id === customerDocumentStatusID)
        setCustomerDocumentStatusName(status ? status.name : "")
    }, [customerDocumentStatuses, customerDocumentStatusID])

    useEffect(() => {
        const status = customerDocumentStatuses.find((element) => element.id === originalCustomerDocumentStatusID)
        setOriginalCustomerDocumentStatusName(status ? status.name : "")
    }, [customerDocumentStatuses, originalCustomerDocumentStatusID])

    useEffect(() => {
        if( ! isNew || ! initCustomFields ) {
            return
        }
        setCustomFields(initCustomFields)
    }, [initCustomFields])
    
    useEffect(() => {
        if (isNew) {
            return
        }
        
        fetchObject()
    }, [isNew, objID])

    useEffect(() => {
        const reference = referenceWithoutVersion + ( version === "" ? "" : " " + version )
        setReference(reference)
    }, [referenceWithoutVersion, version])

    useEffect(() => {
        if( isNew ) {
            setBrowserTabTitle('New Customer Document')
            return
        }
        if( reference || title ) {
            setBrowserTabTitle(`${reference} ${title}`.trim())
        }
    }, [isNew, reference, title, setBrowserTabTitle])

    useEffect(() => {
      if( isNew ) {
        return;
      }
      setTraceabilityLoading(true);
      fetch('/v0/customer_documents/' + objID + '/traceability', { credentials: 'same-origin' })
        .then(function(response) {
          if (!response.ok) throw response;
          return response.json();
        })
        .then(function(json) {
          console.log("CustomerDocument Get Traceability", json.result)
          setTraceability(json.result);
          setTraceabilityLoading(false);
        })
        .catch(error => {
          console.log("CustomerDocument Get Traceability error", error);
          setTraceabilityLoading(false);
        });
    }, [objID, isNew]);
  
   // Convert object to lowercase json
    const toBackend = () => {
        let data = {
            project_id: projectID,
            std_document_id: stdDocumentID,
            reference: reference,
            reference_without_version: referenceWithoutVersion,
            version: version,
            title: title,
            note: note,
            element_code: elementCode,
            organisation_code: organisationCode,
            create_date: createDate,
            receive_date: receiveDate,
            customer_document_status_id: customerDocumentStatusID,
            final_version_file_path: finalVersionFilePath,
            file_paths: filePaths,
            custom_fields: customFields,
            customer_document_models: customerDocumentModels,
            related_items: relatedItems,
            updated_at: updatedAt,
            withdraw_all_versions: withdrawAllVersions,
        }
        if( !isNew ) {
            data.id = id
        }
        return data
    }

    const fetchObject = () => {
        fetch('/v0/customer_documents/'+objID, { credentials: 'same-origin' })
            .then(function(response) {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error(response.statusText)
                }
            })
            .then(function(json) {
                if(json.error) {
                    throw new Error(json.error)
                }

                const obj = json.result
                setID(obj.id)
                setStdDocumentID(obj.std_document_id)
                setReference(obj.reference)
                setReferenceWithoutVersion(obj.reference_without_version)
                setVersion(obj.version)
                setTitle(obj.title)
                setCreateDate(obj.create_date)
                setOrganisationCode(obj.organisation_code)
                setElementCode(obj.element_code)
                setReceiveDate(obj.receive_date)
                setNote(obj.note)
                setCustomerDocumentModels(obj.customer_document_models)
                setCustomerDocumentStatusID(obj.customer_document_status_id)
                setOriginalCustomerDocumentStatusID(obj.customer_document_status_id)
                setFinalVersionFilePath(obj.final_version_file_path)
                setFilePaths(obj.file_paths)
                setCustomFields(obj.custom_fields)
                setIsUsed(obj.is_used)
                setCharacteristics(obj.characteristics)
                setRelatedItems(obj.related_items)
                setPreviousVersionRelatedItems(obj.related_items)
                setUpdatedAt(obj.updated_at)
                setHasLoaded(true)
            })
            .catch(err => {
                console.log('Customer Document fetch error', err)
                setHasAccess(false)
            })
    }

    const handleSubmitError = (props) => {
        setSubmitActive(false)
        setSubmitSpinner(false)
        setError([ props.message ])
    }

    const validateBeforeSubmit = () => {
        const status = customerDocumentStatusName
        let errors = []
        if (!stdDocumentID) {
            errors.push('Type is required')
        }

        if (!reference || /^\s+$/.test(reference)) {
            errors.push('Reference is required')
        }

        if (!title || /^\s+$/.test(title)) {
            errors.push('Title is required')
        }

        if (status === 'Filed' && !createDate) {
            errors.push(PscPhrase('CUSTOMER_DOCUMENT_CREATE_DATE_REQUIRED'))
        }

        if (status !== 'Reserved' && status !== 'Withdrawn' && !receiveDate) {
            errors.push(PscPhrase('CUSTOMER_DOCUMENT_RECEIVE_DATE_REQUIRED'))
        }

        if (status === 'Filed'  && finalVersionFilePath === '') {
            errors.push('Issued File Location is required')
        }

        if (errors.length > 0) {
            setError(errors)
            return false
        }
        return true
    }

    const handleSubmit = (event) => {
        if( event ) {
            event.preventDefault()
        }
        if( submitActive || !validateBeforeSubmit() ) {
            return null
        }

        setSubmitActive(true)
        setSubmitSpinner(true)

        let submit = {
            objType: 'customer_documents',
            objID: objID,
            data: toBackend(),
            successCallback: function(json) {
                setSubmitActive(false)
                setSubmitSpinner(false)
                updateCustomerDocument(json.result)
                forceReloadCustomerDocuments()
            }
        }
        return submit
    }

    const withdrawConfirm = useWithdrawConfirm({
        originalStatusName: originalCustomerDocumentStatusName,
        statusName: customerDocumentStatusName,
        formRef,
        saveButtonRef,
        handleSubmit,
    })
    const { withdrawAllVersions, handleSaveClick } = withdrawConfirm

    const statusIsDisabled = (status) => {
        if (!userHasProjectAccessLevel(currentUser, projectID, 'CADM_CONTROLLER')) {
            return true
        }

        if ( status.name === 'Withdrawn' && isNew ) {
            return true
        }

        let hasLaterNotWithdrawn = characteristics.has_later_not_withdrawn
        let hasLaterLiveOrSuperseded = characteristics.has_later_live_or_superseded

        // As per PSC_Status_Logic_v4.docx

        if ( hasLaterNotWithdrawn ) {
            if ( hasLaterLiveOrSuperseded ) {
                if ( status.name === 'Withdrawn' || status.name === 'Superseded' ) {
                    return false
                }
                else {
                    return true
                }
            }
            else {
                if ( status.name === 'Withdrawn' || status.name === 'Filed' ) {
                    return false
                }
                else {
                    return true
                }
            }
        }
        else {
            if ( status.name === 'Withdrawn' || status.name === 'Filed' || status.name === 'Reserved' ) {
                return false
            }
            return true
        }
    }

    const fieldsDisabled = () => {
        if ( id && originalCustomerDocumentStatusID > 1 ) {
            return true
        }

        if ( ! userHasProjectAccessLevel(currentUser,projectID, 'MEMBER') ) {
            return true
        }

        return false
    }

    const notesDisabled = () => {
        if (!userHasProjectAccessLevel(currentUser, projectID, 'MEMBER')) {
            return true
        }
        return false
    }

    if (!hasAccess) {
        return <NoAccess />
    }
    
    if (!hasLoaded) {
        return <Loading />
    }

    let deleteButton = []
    if (id && userHasProjectAccessLevel(currentUser, projectID, 'CADM_CONTROLLER') && !isUsed) {
        var url = '/v0/customer_documents/'+objID;
        deleteButton.push(
            <DeleteButton
                url={url}
                onError={handleSubmitError}
                callback={() => deleteCustomerDocument(objID)}
                extraText="Deleting this item will remove it completely from GNC Go, including anywhere it is used or referenced - in Reports, Deliveries and elsewhere. (To keep any references to this item in GNC Go you can Withdraw it instead.)"
            />
        )
    }


    let requiredForReservedIndicator = '';
    if ((customerDocumentStatusID === 1) // Reserved 
        && !fieldsDisabled() && userHasProjectAccessLevel(currentUser, projectID, 'MEMBER')) {
        requiredForReservedIndicator = (<label className="form-required-star">&nbsp;*</label>)
    }

    let requiredForLiveIndicator = '';
    if ((customerDocumentStatusID === 2)  // Filed
        && !fieldsDisabled() && userHasProjectAccessLevel(currentUser, projectID, 'CADM_CONTROLLER')) {
        requiredForLiveIndicator = (<label className="form-required-star">&nbsp;*</label>)
    }

    let requiredFileIndicator = false;
    if ((customerDocumentStatusID === 2)  // Filed
        && userHasProjectAccessLevel(currentUser, projectID, 'CADM_CONTROLLER')) {
        requiredFileIndicator = true
    }

    const applicabilityDisabled = userHasProjectAccessLevel(currentUser,projectID, "CADM_CONTROLLER")
    ? false
    : fieldsDisabled()

    // Need to refresh "status is disabled" data -- it depends on data from the backend
    customerDocumentStatuses.forEach((element) => element.disabled = statusIsDisabled(element))

    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            <div style={{display: 'flex', alignItems: 'baseline', flexWrap: 'wrap'}}>
                              <HeaderStatusInfo
                                  type="Customer Document"
                                  id={id}
                                  status={originalCustomerDocumentStatusName}
                              />
                              <DocumentVersions 
                                isHidden={isNew}
                                reference={reference}
                                title={title}
                                documentStatusName={customerDocumentStatusName}
                                projectType={project.project_type.name}
                                traceability={traceability}
                                traceabilityLoading={traceabilityLoading}
                              />
                            </div>
                            
                            <Title
                                type="Customer Document"
                                reference={reference}
                                title={title}
                                id={id}
                                showLatest={true}
                            />
                            
                            <FileButtons
                                finalVersionFilePath={finalVersionFilePath}
                                buttonClass={originalCustomerDocumentStatusName === "Superseded" ? "btn-warning" : ""}
                                visible={originalCustomerDocumentStatusName === "Filed" || originalCustomerDocumentStatusName === "Superseded"}
                                entityType="Document"
                            />
                        </div>
                        <div className="ibox-content">
                            <form ref={formRef} className="form-horizontal" onSubmit={(e) => handleSubmit(e)}>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('LABEL_STD_DOCUMENT')}
                                        {requiredForReservedIndicator}{requiredForLiveIndicator}
                                    </label>

                                    <div className="col-sm-8">
                                        { fieldsDisabled() &&
                                            <input className="form-control" type="text" disabled={true}
                                                value={stdDocument.name} />
                                            ||
                                            <SingleSelect
                                                value={stdDocumentID}
                                                onChange={(value) => setStdDocumentID(value.std_document_id)}
                                                options={getUnarchivedStdDocuments()}
                                                required
                                            />
                                        }
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2"></label>
                                    <div className="col-sm-8">
                                        <h3>{PscPhrase('CUSTOMER_DOCUMENT_HEADER_INFORMATION')}</h3>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('LABEL_REFERENCE')}
                                        {requiredForReservedIndicator}{requiredForLiveIndicator}
                                        <br/>
                                        <small style={{fontWeight: 'normal'}}>(Including Version)</small>
                                    </label>

                                    <div className="col-sm-8 customerdocument-refererence">
                                        <input className="form-control" type="text" value={referenceWithoutVersion} onChange={(event) => setReferenceWithoutVersion(event.target.value)}
                                            required
                                            disabled={fieldsDisabled()}
                                        />
                                        <span className="help-block m-b-none m-t-none">
                                            <small className="form-info-text" > As given on document (with version, if not added below) </small>
                                        </span>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase("LABEL_VERSION")}
                                    </label>

                                    <div className="col-sm-8 customerdocument-version">
                                        <input className="form-control" type="text" value={version} onChange={(event) => setVersion(event.target.value)}
                                                maxLength="10"
                                                disabled={fieldsDisabled()}
                                        />
                                        <span className="help-block m-b-none m-t-none">
                                            <small className="form-info-text" > Optional, but needed to allow upversions and for baseline tracking </small>
                                        </span>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('LABEL_TITLE')}
                                        {requiredForReservedIndicator}{requiredForLiveIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text" name="title" value={title} onChange={(event) => setTitle(event.target.value)}
                                            required
                                            disabled={fieldsDisabled()}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2"></label>
                                    <div className="col-sm-8">
                                        <h3>{PscPhrase('CUSTOMER_DOCUMENT_HEADER_ADDITIONAL')}</h3>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('CUSTOMER_DOCUMENT_LABEL_ORGANISATION_CODE')}
                                        {requiredForReservedIndicator}{requiredForLiveIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text"
                                            value={organisationCode}
                                            onChange={(event) => setOrganisationCode(event.target.value)}
                                            maxLength="8"
                                            required
                                            disabled={fieldsDisabled()}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('CUSTOMER_DOCUMENT_LABEL_ELEMENT_CODE')}</label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text"
                                            value={elementCode}
                                            onChange={(event) => setElementCode(event.target.value)}
                                            maxLength="4"
                                            disabled={fieldsDisabled()}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('CUSTOMER_DOCUMENT_LABEL_RECEIVE_DATE')}
                                        {requiredForLiveIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <PickDate
                                            value={receiveDate}
                                            onChange={setReceiveDate}
                                            disabled={fieldsDisabled()}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('CUSTOMER_DOCUMENT_LABEL_NOTE')}</label>
                                    <div className="col-sm-8">
                                        <TextArea className="form-control" name="note" value={note} onChange={(event) => setNote(event.target.value)} disabled={notesDisabled()} />
                                    </div>
                                </div>

                                <CustomFields
                                    customFields={customFields}
                                    setCustomFields={setCustomFields}
                                    cadmObjectType='customer_documents'
                                    disabled={notesDisabled()}
                                />

                                <Applicability
                                    projectId={projectID}
                                    projectType={project.project_type.name}
                                    options={project.project_systems}
                                    value={customerDocumentModels}
                                    onChange={(value) => setCustomerDocumentModels(value)}
                                    disabled={applicabilityDisabled}
                                />

                                <Status
                                    value={customerDocumentStatusID}
                                    onChange={(value) => setCustomerDocumentStatusID(value) }
                                    statuses={customerDocumentStatuses}
                                />

                                <div className="form-group">
                                  <label className="col-sm-2 control-label">{PscPhrase('CUSTOMER_DOCUMENT_LABEL_CREATE_DATE')}
                                    {requiredForLiveIndicator}
                                  </label>
                                  <div className="col-sm-8">
                                    <PickDate
                                        value={createDate}
                                        onChange={setCreateDate}
                                        disabled={fieldsDisabled()}
                                    />
                                  </div>
                                </div>

                                <div className="hr-line-dashed"></div>

                                <FilePaths
                                    disabled={!userHasProjectAccessLevel(currentUser, projectID, 'CADM_CONTROLLER')}
                                    filePaths={filePaths}
                                    finalVersionFilePath={finalVersionFilePath}
                                    setFilePaths={(value) => setFilePaths(value)}
                                    setFinalVersionFilePath={(value) => setFinalVersionFilePath(value)}
                                    entityType="Document"
                                    filePathsInUse={customerDocumentStatusName === 'Filed' || customerDocumentStatusName === 'Superseded'}
                                    finalVersionFilePathInUse={customerDocumentStatusName === 'Filed' || customerDocumentStatusName === 'Superseded'}
                                    showFileLabels={false}
                                    showRequired={requiredFileIndicator}
                                />

                                <div className="hr-line-dashed"></div>

                                <RelatedItems
                                    object_type="customer_documents"
                                    object_reference_without_version={referenceWithoutVersion}
                                    project_id={projectID}
                                    project_type={project.project_type.name}
                                    related_items={relatedItems}
                                    previous_version_related_items={previousVersionRelatedItems}
                                    disabled={ ! userHasProjectAccessLevel(currentUser,projectID, "MEMBER") }
                                    onChange={(value) => setRelatedItems(value)}
                                />

                                <div className="hr-line-dashed"></div>

                                <div className="form-group">
                                    <ErrorMessage
                                        error={error}
                                    />

                                    <div className="col-sm-10 col-sm-offset-2">

                                        <CancelButton/>

                                        <SaveButton
                                            buttonVariant='primary'
                                            visible={userHasProjectAccessLevel(currentUser,projectID, "MEMBER")}
                                            spinnerActive={submitSpinner}
                                            label='Save Changes'
                                            onClick={handleSaveClick}
                                            onError={handleSubmitError}
                                            ref={saveButtonRef}
                                        />

                                        {deleteButton}

                                        <ManualUpversion
                                                id={objID}
                                                project_id={projectID}
                                                referenceWithoutVersion={referenceWithoutVersion}
                                                version={version}
                                                status={customerDocumentStatusName}
                                                originalStatus={originalCustomerDocumentStatusName}
                                                characteristics={characteristics}
                                                user={currentUser}
                                            />
                                    </div>
                                </div>
                            </form>

                            <WithdrawAllVersionsModal
                                entityLabel="Customer Document"
                                {...withdrawConfirm}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
};

export default CustomerDocument
